import useSWR from "swr";
import { Link } from "react-router-dom";
import { PenLine } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import { fetcher, safeImage } from "@/lib/api";

const STATUS = {
  review: { label: "En revisión", variant: "secondary" },
  approved: { label: "Aprobada", variant: "default" },
  wp_draft: { label: "Borrador en WordPress", variant: "outline" },
  published: { label: "Publicada", variant: "outline" },
};

export default function Redaccion() {
  const [status, setStatus] = useState("all");
  const [limit, setLimit] = useState(40);
  const { data } = useSWR(
    `/articles?limit=${limit}${status === "all" ? "" : `&status=${status}`}`,
    fetcher,
    { refreshInterval: 20000 }
  );

  return (
    <div>
      <PageHeader
        kicker="Producción editorial"
        title="Cola de redacción y aprobación"
        description="Borradores generados por la IA listos para edición, auditoría SEO, aprobación y publicación."
      >
        <Button asChild variant="outline" data-testid="new-from-ingesta">
          <Link to="/ingesta"><PenLine className="h-4 w-4 mr-2" />Nueva desde ingesta</Link>
        </Button>
      </PageHeader>

      <Tabs value={status} onValueChange={setStatus} className="mb-6">
        <TabsList>
          <TabsTrigger value="all" data-testid="filter-all">Todas</TabsTrigger>
          <TabsTrigger value="review" data-testid="filter-review">En revisión</TabsTrigger>
          <TabsTrigger value="approved" data-testid="filter-approved">Aprobadas</TabsTrigger>
          <TabsTrigger value="wp_draft" data-testid="filter-wpdraft">Borrador WP</TabsTrigger>
          <TabsTrigger value="published" data-testid="filter-published">Publicadas</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-3" data-testid="articles-list">
        {(data?.items || []).map((a, i) => (
          <Link
            key={a.id}
            to={`/redaccion/${a.id}`}
            className="block border border-border bg-card p-5 hover:-translate-y-1 transition-transform rise"
            style={{ animationDelay: `${i * 30}ms` }}
            data-testid={`article-card-${a.id}`}
          >
            <div className="flex flex-col md:flex-row gap-4">
              {a.featured_image && (
                <img src={safeImage(a.featured_image)} alt=""
                    onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full md:w-40 h-28 object-cover rounded-sm" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                  <Badge variant={STATUS[a.status]?.variant || "secondary"}>
                    {STATUS[a.status]?.label || a.status}
                  </Badge>
                  <span className="kicker">{a.primary_outlet}</span>
                  {a.category && <span className="text-xs text-muted-foreground">· {a.category}</span>}
                  {a.seo?.puntaje ? (
                    <span className="text-xs mono text-primary">SEO {a.seo.puntaje}</span>
                  ) : null}
                </div>
                <h3 className="text-xl font-bold leading-snug">{a.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{a.dek}</p>
                <div className="text-xs text-muted-foreground mt-2 mono">
                  {a.model} · {a.created_at?.slice(0, 16).replace("T", " ")}
                  {a.pending_checks?.length ? ` · ${a.pending_checks.length} datos por verificar` : ""}
                </div>
              </div>
            </div>
          </Link>
        ))}
        {data && data.total > (data.items || []).length && (
          <div className="flex items-center justify-between gap-3 pt-2" data-testid="articles-pager">
            <span className="text-xs mono text-muted-foreground">
              {data.items.length} de {data.total} noticias
            </span>
            <Button size="sm" variant="outline" onClick={() => setLimit(limit + 40)}
                    data-testid="load-more-articles">Cargar más</Button>
          </div>
        )}
        {data?.items?.length === 0 && (
          <p className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center">
            No hay noticias en este estado.
          </p>
        )}
      </div>
    </div>
  );
}
